// Goal theme registry — visual presets for the fundraising goal thermometer/meter.
//
// Each theme defines a `shape` (how the progress is drawn) and a color palette.
// Shapes:
//   thermometer — vertical classic fundraising thermometer with bulb + scale
//   bar         — horizontal progress bar (raised vs goal)
//   radial      — circular percentage ring
//   badge       — compact circular badge/flag showing raised + % of goal
//
// theme (light|dark) drives the surrounding card background so the section reads
// well on both light and dark layouts.

export const GOAL_THEMES = {
  'classic-red': {
    id: 'classic-red',
    name: 'Classic Red',
    shape: 'thermometer',
    theme: 'light',
    fill: '#e23b3b',
    track: '#e5e7eb',
    accent: '#e23b3b',
    labelColor: '#e23b3b',
    cardBg: '#ffffff',
  },
  'midnight-blue': {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    shape: 'thermometer',
    theme: 'dark',
    fill: '#1fa2ff',
    track: '#1e2a4a',
    accent: '#38bdf8',
    labelColor: '#38bdf8',
    cardBg: '#0b1729',
  },
  'nature-green': {
    id: 'nature-green',
    name: 'Nature Green',
    shape: 'thermometer',
    theme: 'light',
    fill: '#5aa832',
    track: '#e5e7eb',
    accent: '#5aa832',
    labelColor: '#4b8f28',
    cardBg: '#ffffff',
  },
  'royal-purple': {
    id: 'royal-purple',
    name: 'Royal Purple',
    shape: 'thermometer',
    theme: 'light',
    fill: '#6b2fb5',
    track: '#ede9f5',
    accent: '#6b2fb5',
    labelColor: '#6b2fb5',
    cardBg: '#faf8ff',
  },
  'gold-luxe': {
    id: 'gold-luxe',
    name: 'Gold Luxe',
    shape: 'thermometer',
    theme: 'dark',
    fill: '#f5b62a',
    track: '#2a2a2a',
    accent: '#f5b62a',
    labelColor: '#f5b62a',
    cardBg: '#101010',
  },
  'radial-ring': {
    id: 'radial-ring',
    name: 'Radial Ring',
    shape: 'radial',
    theme: 'light',
    fill: '#2f7ff0',
    track: '#e5e7eb',
    accent: '#2f7ff0',
    labelColor: '#1f2937',
    cardBg: '#ffffff',
  },
  'bar-green': {
    id: 'bar-green',
    name: 'Green Bar',
    shape: 'bar',
    theme: 'light',
    fill: '#5aa832',
    track: '#e5e7eb',
    accent: '#4b8f28',
    labelColor: '#4b8f28',
    cardBg: '#ffffff',
  },
  'bar-blue': {
    id: 'bar-blue',
    name: 'Blue Bar',
    shape: 'bar',
    theme: 'dark',
    fill: '#1fa2ff',
    track: '#1e2a4a',
    accent: '#38bdf8',
    labelColor: '#e2e8f0',
    cardBg: '#0b1729',
  },
  'badge-flag': {
    id: 'badge-flag',
    name: 'Badge Flag',
    shape: 'badge',
    theme: 'light',
    fill: 'linear-gradient(135deg, #ff7a45 0%, #f5445a 100%)',
    track: '#f3f4f6',
    accent: '#f5445a',
    labelColor: '#f5445a',
    cardBg: '#ffffff',
  },
  'neon-glow': {
    id: 'neon-glow',
    name: 'Neon Glow',
    shape: 'bar',
    theme: 'dark',
    fill: '#00ffaa',
    track: '#1a1a2e',
    accent: '#00ffaa',
    labelColor: '#00ffaa',
    cardBg: '#0d0d1a',
    glow: true,
  },
  'sunset-gradient': {
    id: 'sunset-gradient',
    name: 'Sunset',
    shape: 'bar',
    theme: 'dark',
    fill: 'linear-gradient(90deg, #ff6b6b, #feca57, #ff9ff3)',
    track: '#2d2d3a',
    accent: '#feca57',
    labelColor: '#feca57',
    cardBg: '#1a1a2e',
  },
  'radial-fire': {
    id: 'radial-fire',
    name: 'Fire Ring',
    shape: 'radial',
    theme: 'dark',
    fill: '#ff6b35',
    track: '#2a1a1a',
    accent: '#ff6b35',
    labelColor: '#ff9f6b',
    cardBg: '#1a0d0d',
  },
  'radial-emerald': {
    id: 'radial-emerald',
    name: 'Emerald Ring',
    shape: 'radial',
    theme: 'dark',
    fill: '#10b981',
    track: '#0d2818',
    accent: '#34d399',
    labelColor: '#34d399',
    cardBg: '#0a1f14',
  },
  'charity-brand': {
    id: 'charity-brand',
    name: 'Charity Brand',
    shape: 'bar',
    theme: 'dark',
    fill: 'var(--charity-primary, #0056A3)',
    track: 'rgba(255,255,255,0.1)',
    accent: 'var(--charity-accent, #FDB913)',
    labelColor: '#ffffff',
    cardBg: 'transparent',
  },
  'badge-gold': {
    id: 'badge-gold',
    name: 'Gold Badge',
    shape: 'badge',
    theme: 'dark',
    fill: 'linear-gradient(135deg, #f5b62a 0%, #d4982a 100%)',
    track: '#1a1a1a',
    accent: '#f5b62a',
    labelColor: '#f5b62a',
    cardBg: '#0d0d0d',
  },
  'minimal-dark': {
    id: 'minimal-dark',
    name: 'Minimal Dark',
    shape: 'bar',
    theme: 'dark',
    fill: '#ffffff',
    track: 'rgba(255,255,255,0.1)',
    accent: '#ffffff',
    labelColor: '#ffffff',
    cardBg: 'transparent',
  },
};

export const DEFAULT_GOAL_THEME = 'classic-red';

export function getGoalTheme(id) {
  return GOAL_THEMES[id] || GOAL_THEMES[DEFAULT_GOAL_THEME];
}

export function getGoalThemeOptions() {
  return Object.values(GOAL_THEMES).map((t) => ({ value: t.id, label: t.name, shape: t.shape, theme: t.theme }));
}
